import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Clock3,
  Monitor,
  Video,
  Phone,
} from "lucide-react";
import { createFollowUp } from "../../api/api";

const FollowupSessions = ({ userData }) => {
  // Get today's date
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [formData, setFormData] = useState({
    mode: "Video Call",
    comments: "",
  });
  const [loading, setLoading] = useState(false);

  const showToast = (message, type = 'success') => {
    const existingToast = document.querySelector('.custom-toast');
    if (existingToast) existingToast.remove();

    const toast = document.createElement('div');
    toast.className = `custom-toast toast-${type}`;

    const icon = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    const bgColor = type === 'success' ? '#10B981' : type === 'error' ? '#EF4444' : '#3B82F6';

    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="background: rgba(255,255,255,0.2); width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold;">
          ${icon}
        </div>
        <div style="flex: 1;">${message}</div>
        <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; font-size: 18px;">×</button>
      </div>
    `;

    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: ${bgColor};
      color: white;
      padding: 14px 20px;
      border-radius: 12px;
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
      min-width: 300px;
      max-width: 400px;
      cursor: pointer;
      animation: slideInRight 0.3s ease;
      z-index: 10000;
    `;

    if (!document.querySelector('#toast-styles')) {
      const style = document.createElement('style');
      style.id = 'toast-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(100%); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    }, 3000);

    toast.onclick = () => {
      if (toast && toast.parentElement) {
        toast.style.animation = 'slideOutRight 0.3s ease';
        setTimeout(() => {
          if (toast && toast.parentElement) toast.remove();
        }, 300);
      }
    };
  };

  // Get days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const isDateDisabled = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < todayDate) return true;
    
    // Disable specific dates (you can modify this as needed)
    const disabledDates = []; // Add dates to disable if needed
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return disabledDates.includes(dateStr);
  };

  // Generate time slots from 10 AM to 7 PM with half-hour intervals
  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 10; // 10 AM
    const endHour = 19; // 7 PM (inclusive)
    
    // Loop from 10 AM to 7 PM
    for (let hour = startHour; hour <= endHour; hour++) {
      // For 7 PM, only add 7:00 PM (not 7:30 PM)
      const maxMinute = hour === endHour ? 0 : 30;
      
      for (let minute = 0; minute <= maxMinute; minute += 30) {
        const hour12 = hour % 12 || 12;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const formattedTime = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        slots.push({
          value: timeValue,
          display: formattedTime
        });
      }
    }
    
    return slots;
  };

  const isTimeDisabled = (timeSlot) => {
  if (!selectedDate) return true;

  const todayDate = new Date();
  const selectedDateObj = new Date(currentYear, currentMonth, selectedDate);

  // If not today → all future times allowed
  if (selectedDateObj.toDateString() !== todayDate.toDateString()) {
    return false;
  }

  const [hours, minutes] = timeSlot.value.split(':').map(Number);

  const slotTime = new Date();
  slotTime.setHours(hours, minutes, 0, 0);

  const now = new Date();

  // ⛔ Block if slot is within next 30 minutes OR already passed
  const bufferLimit = new Date(now.getTime() + 30 * 60000);

  return slotTime <= bufferLimit;
};

  const handleDateSelect = (day) => {
    if (day && !isDateDisabled(day)) {
      setSelectedDate(day);
      // Reset selected time when date changes
      setSelectedTime(null);
      
      const dateObj = new Date(currentYear, currentMonth, day);
      const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
      setFormData(prev => ({
        ...prev,
        date: dateObj.toLocaleDateString('en-US', options),
        selectedDate: `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      }));
    }
  };

  const handleTimeSelect = (timeSlot) => {
    setSelectedTime(timeSlot);
    setFormData(prev => ({
      ...prev,
      time: timeSlot.display,
      selectedTime: timeSlot.value
    }));
  };

  const handleModeSelect = (mode) => {
    setFormData(prev => ({
      ...prev,
      mode: mode
    }));
  };

  const changeMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
    // Reset selections when changing month
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const getMonthName = (month) => {
    const monthNames = ["January", "February", "March", "April", "May", "June", 
                       "July", "August", "September", "October", "November", "December"];
    return monthNames[month];
  };

  const handleConfirmBooking = async () => {
    if (!selectedDate || !selectedTime) {
      showToast("Please select both date and time", "error");
      return;
    }

    setLoading(true);

    try {
      const followUpData = {
        mode_consultation: formData.mode === "Video Call" ? "Video" : "Voice",
        appointment_date: formData.selectedDate,
        appointment_time: formData.selectedTime,
        comments: formData.comments || "Follow-up session",
        type: "followup-session"
      };

      const response = await createFollowUp(followUpData);

      if (response && (response.status === true || response.success === true)) {
        showToast("Follow-up session booked successfully!", "success");
        // Reset selections after successful booking
        setSelectedDate(null);
        setSelectedTime(null);
        setFormData(prev => ({
          ...prev,
          date: "",
          time: "",
          selectedDate: "",
          selectedTime: ""
        }));
      } else {
        showToast(response?.message || "Failed to book follow-up session. Please try again.", "error");
      }
    } catch (error) {
      console.error("Booking error:", error);
      showToast(error.message || "Failed to book follow-up session. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get all time slots
  const allTimeSlots = generateTimeSlots();

  // Format date for display in summary
  const getFormattedDate = () => {
    if (!selectedDate) return "Select a date";
    const dateObj = new Date(currentYear, currentMonth, selectedDate);
    const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
    return dateObj.toLocaleDateString('en-US', options);
  };

  return (
    <div className="w-full font-lato">
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight">
          Book Follow-up Session
        </h2>
        <p className="text-[#98a19b] text-sm mt-2">
          Schedule your next consultation with your dedicated specialist.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-[#e8ece8] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#58b96b] text-white text-[13px] font-semibold flex items-center justify-center">
                1
              </span>
              <h3 className="text-[18px] font-semibold text-[#24312d]">
                Select Date
              </h3>
            </div>

            <div className="flex items-center justify-between mb-6">
              <h4 className="text-[14px] font-semibold text-[#24312d]">
                {getMonthName(currentMonth)} {currentYear}
              </h4>

              <div className="flex items-center gap-2">
                <button 
                  onClick={() => changeMonth('prev')}
                  className="w-8 h-8 border border-[#dfe5df] rounded-md flex items-center justify-center hover:bg-gray-50 transition"
                >
                  <ChevronLeft size={14} />
                </button>

                <button 
                  onClick={() => changeMonth('next')}
                  className="w-8 h-8 border border-[#dfe5df] rounded-md flex items-center justify-center hover:bg-gray-50 transition"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-3 mb-4">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div
                  key={day}
                  className="text-center text-[12px] text-[#a0a8a2] font-medium"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-3">
              {calendarDays.map((day, i) => {
                const isActive = day === selectedDate;
                const isDisabled = day !== null && isDateDisabled(day);

                return (
                  <button
                    key={i}
                    onClick={() => handleDateSelect(day)}
                    className={`h-[52px] rounded-lg text-[14px] font-medium transition ${
                      day === null
                        ? "cursor-default"
                        : isActive
                          ? "bg-[#58b96b] text-white shadow-sm"
                          : isDisabled
                            ? "text-[#d1d6d2] cursor-not-allowed bg-gray-50"
                            : "text-[#24312d] hover:bg-[#f7f9f7] hover:border hover:border-[#58b96b]"
                    }`}
                    disabled={isDisabled || day === null}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
          
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-7 h-7 rounded-full bg-[#58b96a] text-white text-[13px] font-medium flex items-center justify-center">
                2
              </span>
              <h3 className="text-[15px] font-semibold text-[#24312d]">
                Select Time (10 AM - 7 PM)
              </h3>
            </div>

            {/* Show message only when no date is selected */}
            {!selectedDate ? (
              <div className="text-center py-8 text-[#98a19b] text-sm">
                Please select a date first
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {allTimeSlots && allTimeSlots.length > 0 ? (
                  allTimeSlots.map((slot, index) => {
                    const disabled = isTimeDisabled(slot);
                    const isActive = selectedTime && selectedTime.value === slot.value;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => !disabled && handleTimeSelect(slot)}
                        disabled={disabled}
                        className={`h-[42px] rounded-md border text-[13px] transition ${
                          disabled
                            ? "bg-gray-50 border-gray-200 text-[#d1d6d2] cursor-not-allowed"
                            : isActive
                              ? "bg-[#58b96a] border-[#58b96a] text-white shadow-sm"
                              : "border-[#e1e6e1] text-[#24312d] hover:border-[#58b96a] hover:bg-[#f7fcf8]"
                        }`}
                      >
                        {slot.display}
                      </button>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center text-[#98a19b] text-sm">
                    No time slots available
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-7 h-7 rounded-full bg-[#58b96a] text-white text-[13px] font-medium flex items-center justify-center">
                3
              </span>
              <h3 className="text-[15px] font-semibold text-[#24312d]">
                Choose Mode
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => handleModeSelect("Video Call")}
                className={`border rounded-xl p-6 text-center transition ${
                  formData.mode === "Video Call"
                    ? "border-[#58b96a] bg-[#f7fcf8] shadow-sm"
                    : "border-[#e1e6e1] hover:border-[#58b96a]"
                }`}
              >
                <div className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-4 ${
                  formData.mode === "Video Call" ? "bg-[#58b96a]" : "bg-[#f7f9f7]"
                }`}>
                  <Video size={18} className={formData.mode === "Video Call" ? "text-white" : "text-[#58b96a]"} />
                </div>

                <h4 className="text-[15px] font-semibold text-[#24312d] mb-1">
                  Video Call
                </h4>

                <p className="text-[12px] text-[#97a09a]">
                  Secure video consultation
                </p>
              </button>

              <button
                onClick={() => handleModeSelect("Voice Call")}
                className={`border rounded-xl p-6 text-center transition ${
                  formData.mode === "Voice Call"
                    ? "border-[#58b96a] bg-[#f7fcf8] shadow-sm"
                    : "border-[#e1e6e1] hover:border-[#58b96a]"
                }`}
              >
                <div className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center mb-4 ${
                  formData.mode === "Voice Call" ? "bg-[#58b96a]" : "bg-[#f7f9f7]"
                }`}>
                  <Phone size={18} className={formData.mode === "Voice Call" ? "text-white" : "text-[#58b96a]"} />
                </div>

                <h4 className="text-[15px] font-semibold text-[#24312d] mb-1">
                  Voice Call
                </h4>

                <p className="text-[12px] text-[#97a09a]">
                  Standard audio consultation
                </p>
              </button>
            </div>
          </div>
        </div>
        
        <div className="bg-white border border-[#e8ece8] rounded-xl p-6 h-fit">
          <h3 className="text-[18px] font-semibold text-[#24312d] mb-6">
            Booking Summary
          </h3>

          <div className="text-center mb-6">
            {userData?.profile_img ? (
              <img
                src={userData.profile_img}
                alt={userData.name || "User"}
                className="w-16 h-16 rounded-full object-cover mx-auto mb-4"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#57b26a] mx-auto flex items-center justify-center mb-4">
                <span className="text-white text-xl font-semibold">
                  {userData?.name ? userData.name.charAt(0).toUpperCase() : "U"}
                </span>
              </div>
            )}
            <h4 className="text-[18px] font-semibold text-[#24312d] leading-tight">
              {userData?.name || "User"}
            </h4>

            <p className="text-[13px] text-[#98a19b] mt-1">
              Customer
            </p>
          </div>

          <div className="border-t border-[#edf1ed] pt-5 space-y-5">
            <div className="flex gap-3">
              <CalendarDays size={16} className="text-[#98a19b] mt-1" />
              <div>
                <p className="text-[12px] text-[#98a19b]">Date</p>
                <p className="text-[14px] font-medium text-[#24312d] mt-1">
                  {selectedDate ? getFormattedDate() : "Select a date"}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Clock3 size={16} className="text-[#98a19b] mt-1" />
              <div>
                <p className="text-[12px] text-[#98a19b]">Time</p>
                <p className="text-[14px] font-medium text-[#24312d] mt-1">
                  {selectedTime ? selectedTime.display : "Select a time"}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Monitor size={16} className="text-[#98a19b] mt-1" />
              <div>
                <p className="text-[12px] text-[#98a19b]">
                  Consultation Mode
                </p>
                <p className="text-[14px] font-medium text-[#24312d] mt-1">
                  {formData.mode}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-[#edf1ed] mt-6 pt-5 flex items-center justify-between">
            <span className="text-[14px] text-[#98a19b]">Session Fee</span>
            <span className="text-[14px] font-semibold text-[#58b96b]">
              Included in Plan
            </span>
          </div>

          <button
            onClick={handleConfirmBooking}
            disabled={loading || !selectedDate || !selectedTime}
            className={`w-full h-[44px] mt-6 rounded-md text-white text-[14px] font-medium transition ${
              loading || !selectedDate || !selectedTime
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#58b96b] hover:bg-[#4a9e5a]"
            }`}
          >
            {loading ? "Booking..." : "Confirm Booking"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowupSessions;